import React, {useRef, useState} from 'react'
import {useLoaderData, useLocation, useSearchParams} from "react-router";
import {Field} from "../../ui/elements/field";
import * as API from '@/api'
import {Form} from "../../ui/elements/form";
import {ArrowUpOnSquareIcon, EllipsisVerticalIcon, PlayCircleIcon, VideoCameraIcon} from "@heroicons/react/24/solid";
import {MagnifyingGlassIcon} from "@heroicons/react/24/outline";
import {NewPlayer} from "../../models/vlogs/new_player";
import {useDebouncedCallback} from "use-debounce";

const truncateString = (str, maxLength) => {
  if (maxLength === null) return str

  const ellipsis = "...";
  if (str.length > maxLength) {
    // Return the truncated string plus the ellipsis.
    // Adjust slice length to user for the ellipsis length.
    return str.slice(0, maxLength - ellipsis.length) + ellipsis;
  } else {
    return str;
  }
};

const ClipResult = props => {
  const { vlog, timestamp } = props
  const [play, setPlay] = React.useState(false);

  return <>
    <button type="button" onClick={() => setPlay(!play)} className="snap-center  w-full flex flex-col gap-2 max-h-full hover:cursor-pointer">
      <div className="flex flex-col md:flex-row items-start gap-4 w-full">
        <div className="md:w-1/3 flex aspect-square justify-center h-full w-full">
          {play ?
            <div className="h-full"><NewPlayer vlog={vlog} timestamps={vlog.timestamps} start={timestamp.start_seconds}
                                               autoPlay/></div> :
            <div className="flex  bg-[#4F98BD] border justify-center w-full h-full border-slate-300 items-center rounded-lg"><PlayCircleIcon className="size-20" /></div>}
        </div>
        <div className="md:w-2/3 overflow-ellipsis h-25 whitespace-normal text-justify"
             dangerouslySetInnerHTML={{__html: truncateString(timestamp.text, null)}}/>
      </div>
    </button>
  </>
}

const Menu = props => {
  const { vlog, show, timestamp } = props

  const share = () => {
    if (navigator.share) {
      navigator.share({
        title: `${vlog.title} @ ${timestamp.start_seconds}s`,
        url: timestamp.youtube_link
      })
    }
  }

  return <ul className={`absolute group-focus-within:block ${show ? '' : 'hidden'} bg-[#2B4B9F] [&>li]:border-b [$>li:last]:border-none flex flex-col whitespace-nowrap rounded-lg top-7 right-0 mt-4 border z-20`}>
    {navigator.share && <li><button type="button" onClick={share} className="flex items-center gap-2 py-4 px-4 hover:cursor-pointer hover:bg-[#4F98BD] hover:backdrop-blur-lg"><ArrowUpOnSquareIcon className="size-6" /> Share</button></li>}
    <li className="flex items-center gap-2 py-4 px-4 hover:cursor-pointer hover:bg-[#4F98BD] hover:backdrop-blur-lg">
      <VideoCameraIcon className="w-6"/> Vlog ${vlog.id}</li>
  </ul>
}

const ClipsCarousel = props => {
  const { vlog } = props
  const carouselRef = useRef({});
  const [activeIndex, setActiveIndex] = React.useState(0);

  React.useEffect(() => {
    if (!carouselRef.current) return;

    const listener = carouselRef.current.addEventListener('scroll', (evt) => {
      const clipResults = Array.from(carouselRef.current.querySelectorAll('.clip-result'))
      clipResults.forEach(function (ele, index) {
        if (Math.abs(ele.offsetLeft - evt.target.scrollLeft) < 10) {
          setActiveIndex(index)
        } else {
        }
      });
    })

    return () => {
      if (!carouselRef.current) return

      carouselRef.current.removeEventListener('scroll', listener)
    }
  }, [])

  return <>
    <ul ref={carouselRef} className="flex flex-nowrap gap-4 w-full h-full overflow-x-scroll overflow-y-hidden whitespace-nowrap snap-x snap-mandatory snap-always relative">
      {vlog.timestamps && vlog.timestamps.length > 0 && vlog.timestamps.map(timestamp =>
        <li key={timestamp.id} className="clip-result min-w-full">
          <ClipResult vlog={vlog} timestamp={timestamp} />
        </li>
      )}
    </ul>
    {vlog.timestamps.length > 1 && <div className="flex items-center justify-center w-full gap-2">
      {vlog.timestamps.map((timestamp, index) => <div key={timestamp.id} className={`size-2 border border-slate-300 rounded-full ${activeIndex === index && 'bg-slate-300'}`} />)}
    </div>}
  </>
}

const Timestamp = props => {
  const { vlog, timestamp } = props
  const [showMenu, setShowMenu] = React.useState(false)

  return <>
    <div className="relative flex gap-2 items-center justify-between">
      <div className="w-full flex flex-col gap-2">
        <div className="flex items-center justify-between gap-2">
          <h4 className="font-[Axiforma_Black] text-2xl line-clamp-2 whitespace-nowrap">
            {vlog.title}
          </h4>
          <div className="relative flex items-center group z-50">
            <button type="button" className="hover:cursor-pointer" onClick={() => setShowMenu(!showMenu)}>
              <div>
                <EllipsisVerticalIcon className="size-8" />
              </div>
            </button>
            <Menu timestamp={timestamp} vlog={vlog} show={showMenu} />
          </div>
        </div>
        <div className="flex items-center justify-between w-full">
          <div>{vlog.recorded_on_human}</div>
          <div className="whitespace-nowrap">
            {vlog.timestamps.length}
            {' '}
            {vlog.timestamps.length !== 1 ? 'matches' : 'match'}
          </div>
        </div>
      </div>
    </div>
    <div className="flex flex-col gap-2 w-full justify-center relative rounded-lg">
      <ClipsCarousel vlog={vlog} />
    </div>
  </>
}

export const VlogsDirectory = props => {
  const allVlogs = useLoaderData()
  const [searchResults, setSearchResults] = useState({})
  const debouncedSearch = useDebouncedCallback(
    value => {
      setSearchParams({q: value})
      search(value)
    },
    300
  )
  const [searchParams, setSearchParams] = useSearchParams()
  const [searching, setSearching] = React.useState(false)

  const vlogs = searchResults.vlogs || allVlogs || []
  const timestamps = searchResults.timestamps || []
  const searchQ = searchParams.get('q') || ''

  const location = useLocation()

  const search = async (q) => {
    if (!q || q.match(/^ *$/) !== null) {

      setSearchResults({ vlogs: allVlogs })
      return
    }

    setSearching(true)
    return API.Vlogs.search(q).then((results) => {
      setSearching(false)
      setSearchResults(results)
    })
  }

  React.useEffect(() => {
    const currentQ = searchParams.get('q')
    if (currentQ) search(currentQ)
  }, [])

  return <div className="flex flex-col">
    <style type="text/css">
      {`
        .search-result {
          background-color: transparent;
          color: inherit;
          padding-top: 0.25em;
          font-family: 'Axiforma Heavy';
          letter-spacing: 0.5px
        }
      `}
    </style>
    <div className="flex flex-col gap-8">
      <Form onSubmit={search} className="w-full md:w-1/2 mx-auto items-center">
        <div className="flex flex-col gap-3 w-[400px]">
          <Field value={searchQ}
                 type="text"
                 icon={MagnifyingGlassIcon}
                 onKeyUp={evt => debouncedSearch(evt.target.value)} className="outline p-1 text-xl"
                 placeholder="Search"
          />
        </div>
      </Form>
      {searching ? <div className="animate-bounce w-full text-center text-lg">Searching...</div> : <>
        {vlogs && vlogs.length > 0 && <div className="flex flex-col gap-6">
          <ul className="grid grid-cols-2 md:grid-cols-3 gap-2 auto-rows-fr">
            {vlogs.map((vlog) => <li key={vlog.id}
                                     className="hover:bg-blue-600/50 rounded-lg hover:text-slate-300 w-full h-full">
              <a
                href={`/vlogs/${vlog.id}`}
                className="block h-full w-full transition-all duration-100 rounded-lg bg-cover p-2 bg-no-repeat text-[#4F98BD] hover:text-slate-300"
              >
                <div className="font-[Axiforma_Black] w-full text-lg"
                     dangerouslySetInnerHTML={{__html: vlog.title.replace(RegExp(searchQ, "ig"), `<mark class="search-result">${searchQ}</mark>`)}}/>
                <div className="font-[Axiforma_ExtraBold] text-sm whitespace-nowrap">
                  {vlog.recorded_on}
                </div>
              </a>
            </li>)}
          </ul>
        </div>}
        {timestamps && timestamps.length > 0 && <div className="flex flex-col gap-6">
          <ul className="grid grid-cols-1 gap-4 md:grid-cols-2 justify-center md:justify-between">
            {timestamps && timestamps.map(rootTimestamp => <li key={rootTimestamp.vlog.id}
                                                               className="overflow-hidden  flex flex-col gap-2 p-4 hover:bg-blue-600/50 rounded-lg">
              <Timestamp timestamp={rootTimestamp} vlog={rootTimestamp.vlog}/>
            </li>)}
          </ul>
        </div>}
      </>}
    </div>
  </div>
}
